import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, Button, Tag } from 'antd'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import { z } from 'zod'
import type { BusinessUpsertInput } from '@super-mega/server/contracts/business'
import type { AccountDisplay } from '@super-mega/server/contracts/profile'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { DEFAULT_PAGE_SEARCH as DEFAULT_QUERY } from '@/const/list.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  BUSINESS_TYPE_ENUM,
  BUSINESS_TYPE_TAG_COLOR,
  BusinessBatchCreateModal,
  BusinessEditorModal,
  useBusinessListActions
} from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { usePageTableNavigation, type PageTableSearch } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import { formatAmount } from '@/utils/money.ts'

type BusinessQuery = PageTableSearch & {
  id?: number
  keyword?: string
  type?: 'lease' | 'valueAdded'
  status?: 'enabled' | 'disabled'
}

type BusinessSearchFormValues = Pick<BusinessQuery, 'keyword' | 'type' | 'status'>

type BusinessRow = {
  id: number
  orderCount: number
  name: string
  type: 'lease' | 'valueAdded'
  defaultServiceMode: BusinessUpsertInput['defaultServiceMode']
  color: string
  isEnabled: boolean
  referencePrice: string | null
  costPrice: string | null
  note: string | null
  creator: AccountDisplay
  createdAt: Date
  updatedAt: Date
}

const businessSearchSchema = z.object({
  current: z.coerce.number().int().min(1).catch(DEFAULT_QUERY.current),
  pageSize: z.coerce.number().int().min(1).max(100).catch(DEFAULT_QUERY.pageSize),
  id: z.coerce.number().int().positive().optional().catch(undefined),
  keyword: z.string().trim().max(255).optional().catch(undefined),
  type: z.enum(['lease', 'valueAdded']).optional().catch(undefined),
  status: z.enum(['enabled', 'disabled']).optional().catch(undefined)
})

const listQuery = createListQueryPersistence('super-mega:master-data:businesses:list-query', businessSearchSchema)

export const Route = createFileRoute('/_authenticated/master-data/businesses')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: BusinessesPage,
  head: () => ({ meta: [{ title: '业务管理 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function formatPrice(value?: string | null) {
  return value === null || value === undefined ? '—' : `¥${formatAmount(value)}`
}

function createBusinessColumns({
  canManage,
  onChangeStatus,
  onEdit,
  onRemove
}: {
  canManage: boolean
  onChangeStatus: (business: BusinessRow) => void
  onEdit: (business: BusinessRow) => void
  onRemove: (business: BusinessRow) => void
}): ProColumns<BusinessRow>[] {
  return [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '业务名称或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'id',
      copyable: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      ellipsis: true,
      render: (_, business) => (
        <Tag className="max-w-full truncate" color={business.color} title={business.name}>
          {business.name}
        </Tag>
      ),
      search: false,
      title: '业务名称',
      width: '14rem'
    },
    {
      dataIndex: 'type',
      render: (_, business) => (
        <Tag variant="outlined" color={BUSINESS_TYPE_TAG_COLOR[business.type]}>
          {BUSINESS_TYPE_ENUM[business.type].text}
        </Tag>
      ),
      title: '业务类型',
      valueEnum: BUSINESS_TYPE_ENUM,
      width: '7rem'
    },
    {
      dataIndex: 'defaultServiceMode',
      render: (_, business) => {
        if (business.type === 'lease') {
          return '-'
        }
        if (business.defaultServiceMode === 'term') {
          return (
            <Tag color="cyan" variant="outlined">
              期限服务
            </Tag>
          )
        }
        return (
          <Tag color="blue" variant="outlined">
            一次性服务
          </Tag>
        )
      },
      search: false,
      title: '服务模式',
      width: '8rem'
    },
    {
      dataIndex: 'orderCount',
      align: 'right',
      search: false,
      title: '订单数',
      tooltip: '租赁业务统计全部租赁订单，增值业务统计全部服务订单，不限状态和时间',
      width: '7rem'
    },
    {
      dataIndex: 'referencePrice',
      renderText: formatPrice,
      search: false,
      title: '参考价格',
      width: '9rem'
    },
    {
      dataIndex: 'costPrice',
      renderText: formatPrice,
      search: false,
      title: '成本价格',
      width: '9rem'
    },
    {
      dataIndex: 'note',
      ellipsis: true,
      renderText: (value?: string) => value || '—',
      search: false,
      title: '备注',
      width: '16rem'
    },
    {
      dataIndex: 'status',
      hideInTable: true,
      title: '状态',
      valueEnum: {
        enabled: { text: '已启用' },
        disabled: { text: '已停用' }
      }
    },
    {
      dataIndex: 'isEnabled',
      render: (_, business) => (
        <Tag color={business.isEnabled ? 'success' : 'default'}>{business.isEnabled ? '已启用' : '已停用'}</Tag>
      ),
      search: false,
      title: '状态',
      width: '6.5rem'
    },
    {
      dataIndex: 'creator',
      render: (_, business) => (
        <UserAvatarName avatar={business.creator.avatar} name={business.creator.name} size={28} />
      ),
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      align: 'center',
      fixed: 'right',
      render: (_, business) =>
        canManage ? (
          <TableRowActions
            resource="business"
            actions={[
              { key: 'edit', label: '编辑', onClick: () => onEdit(business) },
              {
                danger: business.isEnabled,
                key: 'status',
                label: business.isEnabled ? '停用' : '启用',
                onClick: () => onChangeStatus(business)
              },
              { danger: true, key: 'delete', label: '删除', onClick: () => onRemove(business) }
            ]}
          />
        ) : (
          '—'
        ),
      search: false,
      title: '操作',
      valueType: 'option',
      width: '10rem'
    }
  ]
}

function BusinessesPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [batchCreateOpen, setBatchCreateOpen] = useState(false)
  const [editing, setEditing] = useState<BusinessRow | 'new'>()
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('business.') && !permission.endsWith('.read'))
  const businesses = useQuery({
    ...trpc.business.list.queryOptions(query),
    placeholderData: keepPreviousData
  })
  const closeEditor = () => setEditing(undefined)
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries(trpc.business.list.queryFilter()),
      queryClient.invalidateQueries(trpc.business.options.queryFilter())
    ])
  const businessActions = useBusinessListActions({
    currentPage: query.current,
    goToPreviousPage: () => navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) }),
    invalidate,
    rows: businesses.data?.data ?? []
  })
  const table = usePageTableNavigation<BusinessQuery, BusinessSearchFormValues>({
    defaultSearch: DEFAULT_QUERY,
    getFormInitialValues: (search) => ({
      keyword: search.keyword ?? (search.id === undefined ? undefined : String(search.id)),
      status: search.status,
      type: search.type
    }),
    getSearchFromForm: (values) => ({
      id: undefined,
      keyword: values.keyword?.trim() || undefined,
      status: values.status,
      type: values.type
    }),
    navigate: navigateSearch,
    onQueryChange: () => {
      closeEditor()
      businessActions.clearSelection()
    },
    search: query,
    showTotal: (total) => `共 ${total} 个业务`
  })

  const columns = createBusinessColumns({
    canManage,
    onChangeStatus: businessActions.confirmChangeStatus,
    onEdit: (business) => setEditing(business),
    onRemove: businessActions.confirmRemoveBusiness
  })

  return (
    <PageContainer
      breadcrumbRender={false}
      content="维护可对外提供的服务项目；标签颜色用于列表中快速识别，价格仅作参考，不影响实际结算。"
      title="业务管理"
    >
      {businesses.isError ? (
        <Alert
          action={
            <Button onClick={() => void businesses.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(businesses.error, '业务列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<BusinessRow, BusinessSearchFormValues>
        columns={columns}
        columnsState={{ persistenceKey: 'master-data-businesses:table', persistenceType: 'localStorage' }}
        dataSource={businesses.data?.data ?? []}
        loading={{ delay: 150, spinning: businesses.isFetching }}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void businesses.refetch() }}
        pagination={{ ...table.pagination, total: businesses.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        rowSelection={
          canManage
            ? {
                onChange: (keys) => businessActions.setSelectedBusinessIds(keys.map(Number)),
                selectedRowKeys: businessActions.selectedBusinessIds
              }
            : undefined
        }
        scroll={{ x: '131rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <AuthorizedDropdown
                  resource="business"
                  key="batch-actions"
                  menu={businessActions.batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    disabled={businessActions.selectedBusinessIds.length === 0}
                    icon={<ListChecks aria-hidden size={16} />}
                  >
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                <Button key="batch-create" onClick={() => setBatchCreateOpen(true)}>
                  批量添加业务
                </Button>,
                <Authorized key="new" permissions={['business.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => setEditing('new')}
                    type="primary"
                  >
                    新建业务
                  </Button>
                </Authorized>
              ]
            : []
        }
      />

      {batchCreateOpen ? (
        <BusinessBatchCreateModal onClose={() => setBatchCreateOpen(false)} onSaved={invalidate} />
      ) : null}
      <BusinessEditorModal
        editing={editing}
        onClose={closeEditor}
        onSaved={async () => {
          closeEditor()
          await invalidate()
        }}
      />
    </PageContainer>
  )
}
